import { useNavigate } from "react-router-dom";
import { useAuth, useTier } from "@/context/AuthContext";

// TrialExpiredLockout — global blocking overlay for `tier === "trial_expired"`.
// Rendered by Layout.jsx *above* <Outlet />; when active it fully replaces
// the outlet so the user cannot navigate anywhere in the app shell.
//
// Rules (per approved P0 plan):
//   • Blocks ONLY users whose resolved tier is `trial_expired`.
//   • Basic, Premium, Admin, and active trial users are never blocked.
//   • Does not touch or delete any user data.
//   • Does not change any subscription / billing logic.
export default function TrialExpiredLockout() {
  const { isExpired, isAdmin, isBasicOrAbove } = useTier();
  const { logout } = useAuth();
  const navigate = useNavigate();

  // Belt-and-braces guard — never block Admin / Basic / Premium.
  if (isAdmin || isBasicOrAbove) return null;
  if (!isExpired) return null;

  const onLogout = async () => {
    await logout();
    navigate("/auth/login", { replace: true });
  };

  return (
    <div
      data-testid="trial-expired-lockout"
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      style={{
        background: "rgba(15, 15, 15, 0.92)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="trial-expired-lockout-title"
    >
      <div
        className="w-full max-w-md rounded-xl p-8 shadow-2xl"
        style={{
          background: "linear-gradient(180deg, #1a1a1a 0%, #14100a 100%)",
          border: "1px solid #C9A86A",
        }}
      >
        <h2
          id="trial-expired-lockout-title"
          className="text-xl font-bold mb-3 text-center"
          style={{ color: "#C9A86A", letterSpacing: "0.5px" }}
          data-testid="trial-expired-lockout-title"
        >
          Trial Expired
        </h2>
        <p
          className="text-sm mb-6 text-center"
          style={{ color: "#f7f7f7", lineHeight: 1.6 }}
          data-testid="trial-expired-lockout-message"
        >
          Your trial has expired. Please purchase the Basic plan to continue using CDI Collectables.
        </p>
        <button
          type="button"
          onClick={() => navigate("/auth/purchase-basic")}
          data-testid="trial-expired-lockout-buy-basic"
          className="w-full inline-flex items-center justify-center px-4 py-3 rounded-md text-sm font-bold mb-3"
          style={{
            background: "#C9A86A",
            color: "#0f0f0f",
            letterSpacing: "0.5px",
          }}
        >
          Buy Basic
        </button>
        <button
          type="button"
          onClick={onLogout}
          data-testid="trial-expired-lockout-logout"
          className="w-full inline-flex items-center justify-center px-4 py-3 rounded-md text-sm font-semibold"
          style={{
            background: "transparent",
            color: "#f7f7f7",
            border: "1px solid #3a3a3a",
            letterSpacing: "0.3px",
          }}
        >
          Log out
        </button>
      </div>
    </div>
  );
}
